import { ChangeEvent, FC } from "react";
import { useTranslation } from "react-i18next";

import {
  formatDurationSecondsCompact,
  NO_VALUE_DISPLAY,
  numericValueWithCommas,
} from "@ryde/shared";

import {
  STATION_TIMING_FIRST_LEG_NUMBER,
  STATION_TIMING_MEASURED_COLUMNS_COUNT,
} from "./station-timing-preview.constants";
import { StationTimingDayToggle } from "./station-timing-preview.day-toggle";
import { StationTimingDelta } from "./station-timing-preview.delta";
import { IStationTimingLegRow } from "./station-timing-preview.models";
import { StationTimingPreviewOption } from "./station-timing-preview.option";
import {
  buildStationTimingLegName,
  computeStationTimingSelectedDelta,
} from "./station-timing-preview.utils";

interface IStationTimingPreviewRowProps {
  row: IStationTimingLegRow;
  selectedOptionKey: string | undefined;
  isDayBreakdownExpanded: boolean;
  onOptionChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onDayBreakdownToggle: (legIndex: number) => void;
}

export const StationTimingPreviewRow: FC<IStationTimingPreviewRowProps> = ({
  row,
  selectedOptionKey,
  isDayBreakdownExpanded,
  onOptionChange,
  onDayBreakdownToggle,
}) => {
  const { t } = useTranslation();
  const legNumber = row.legIndex + STATION_TIMING_FIRST_LEG_NUMBER;
  const legName = buildStationTimingLegName(row);
  const radioGroupName = `station-timing-leg-${row.legIndex}`;
  const plannedLabel =
    formatDurationSecondsCompact(row.plannedSeconds) || NO_VALUE_DISPLAY;
  const spreadRange = `${formatDurationSecondsCompact(row.p10Seconds) || NO_VALUE_DISPLAY} - ${
    formatDurationSecondsCompact(row.p90Seconds) || NO_VALUE_DISPLAY
  }`;
  // The measurements first, the way back out of the leg last: the planned option is an escape hatch,
  // not a candidate to weigh against the others.
  const chipOptions = [
    row.medianOption,
    row.averageOption,
    ...row.trafficOptions,
    row.plannedOption,
  ].filter(option => option !== undefined);
  const selectedDelta = computeStationTimingSelectedDelta({
    row,
    selectedOptionKey,
  });

  return (
    // The leg's options are grouped by the radio `name` they share, so the row keeps its native table
    // semantics and stays navigable as a table row in assistive technology.
    <tr>
      <th scope="row" className="station-timing__leg">
        {/* Legs with and without data render in separate groups, so each row states where it sits. */}
        <span className="station-timing__leg-index">{legNumber}</span>
        {row.stationFromName}
        <span className="station-timing__leg-arrow" aria-hidden="true">
          &#8594;
        </span>
        {row.stationToName}
      </th>
      <td className="station-timing__planned station-timing__numeric">
        {plannedLabel}
      </td>
      {row.hasData ? (
        <>
          <td className="station-timing__measure">
            <div className="station-timing__chips">
              {chipOptions.map(option => (
                <StationTimingPreviewOption
                  key={option.key}
                  option={option}
                  legName={legName}
                  radioGroupName={radioGroupName}
                  isSelected={selectedOptionKey === option.key}
                  onOptionChange={onOptionChange}
                />
              ))}
            </div>
            <p className="station-timing__support">
              <span className="station-timing__spread">
                {t("stationTiming.SPREAD_LABEL")}
                <span className="station-timing__numeric">{spreadRange}</span>
              </span>
              {row.dayRows.length ? (
                <StationTimingDayToggle
                  legIndex={row.legIndex}
                  legName={legName}
                  daysCount={row.dayRows.length}
                  isExpanded={isDayBreakdownExpanded}
                  onToggle={onDayBreakdownToggle}
                />
              ) : null}
            </p>
          </td>
          <td className="station-timing__delta-cell">
            <StationTimingDelta delta={selectedDelta} isCompact={false} />
          </td>
        </>
      ) : (
        <td
          className="station-timing__no-data"
          colSpan={STATION_TIMING_MEASURED_COLUMNS_COUNT}
        >
          {t("stationTiming.INSUFFICIENT_DATA")}
        </td>
      )}
      {/* Kept on a leg with no data too: the sample size is what explains why it is not offered. */}
      <td className="station-timing__based-on">
        <span className="station-timing__numeric">
          {numericValueWithCommas(row.datapoints)}
        </span>
        <span className="station-timing__based-on-unit">
          {t("stationTiming.RIDES_UNIT")}
        </span>
      </td>
    </tr>
  );
};
